import { useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import type { CameraStatus } from '@/mobile/hooks/useCameraStream';

interface ScanErrorStateProps {
  cameraStatus: CameraStatus;
  /** Mensagem de erro customizada (ex: cota OCR esgotada). Sobrepõe a mensagem padrão de câmera. */
  quotaMessage?: string;
  onRetry: () => void;
  onFileSelected: (dataUrl: string) => void;
  onClose: () => void;
}

const ERROR_MESSAGES: Partial<Record<CameraStatus, string>> = {
  denied:
    'O acesso à câmera foi negado. Para usar o scanner, permita o acesso nas configurações do navegador ou do sistema.',
  unsupported:
    'Seu navegador não suporta acesso à câmera. Tente abrir o GestAKI no Chrome ou Safari atualizados.',
};

/**
 * Tela de erro do scanner.
 * Cobre: permissão negada e câmera sem suporte.
 * Fallback: file input para enviar imagem diretamente.
 */
export function ScanErrorState({ cameraStatus, quotaMessage, onRetry, onFileSelected, onClose }: ScanErrorStateProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result;
      if (typeof result === 'string') onFileSelected(result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  const isQuotaError = !!quotaMessage;
  const errorMsg = quotaMessage ?? ERROR_MESSAGES[cameraStatus] ?? ERROR_MESSAGES['denied'];

  return (
    <div
      role="alertdialog"
      aria-label="Câmera indisponível"
      style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--ink)', overflow: 'hidden' }}
    >
      {/* Botão fechar */}
      <button
        onClick={onClose}
        aria-label="Fechar"
        style={{
          position: 'absolute',
          top: 'calc(env(safe-area-inset-top, 0px) + 54px)',
          right: 18, width: 36, height: 36, borderRadius: '50%',
          background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)',
          color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', zIndex: 10,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Conteúdo central */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 36px', gap: 20, textAlign: 'center' }}>
        {/* Glyph câmera com X */}
        <div aria-hidden="true" style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', display: 'grid', placeItems: 'center', position: 'relative' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="1.6" aria-hidden="true">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          <span style={{ position: 'absolute', bottom: 10, right: 10, width: 20, height: 20, borderRadius: '50%', background: 'var(--block)', display: 'grid', placeItems: 'center' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </span>
        </div>

        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>
            {isQuotaError ? 'Cota mensal atingida' : 'Câmera indisponível'}
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', marginTop: 8, lineHeight: 1.55, maxWidth: 280 }}>
            {errorMsg}
          </div>
        </div>

        {/* Ações */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 280 }}>
          {!isQuotaError && (
            <button
              onClick={onRetry}
              aria-label="Tentar abrir câmera novamente"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 18px', borderRadius: 'var(--r-3)', background: 'var(--mint-deep)', color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer', minHeight: 44 }}
            >
              <RefreshCw size={16} strokeWidth={1.8} aria-hidden />
              Tentar novamente
            </button>
          )}

          {!isQuotaError && (
            <button
              onClick={() => fileInputRef.current?.click()}
              aria-label="Escolher imagem da galeria"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 18px', borderRadius: 'var(--r-3)', background: 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer', minHeight: 44 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              Escolher imagem
            </button>
          )}

          <button
            onClick={onClose}
            aria-label="Fechar scanner"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px 18px', borderRadius: 'var(--r-3)', background: isQuotaError ? 'var(--mint-deep)' : 'rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 600, border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer', minHeight: 44 }}
          >
            {isQuotaError ? 'Entendido' : 'Fechar'}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            aria-hidden="true"
            tabIndex={-1}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}
